import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import DiscoverySection from '../explore/DiscoverySection';
import {
	COVERAGE_MAP_META,
	COVERAGE_CATEGORIES,
	PRIORITY_BREADTH_TARGETS,
	SOURCE_TIERS,
	coverageBarBlocks,
	statusLabel,
} from '../../data/coverageMapData';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/explore.scss';

function CoverageBar({ coverage }) {
	const { filled, empty } = coverageBarBlocks(coverage);
	return (
		<span className="coverage-map-bar" aria-label={`${coverage}% covered`}>
			<span className="coverage-map-bar__blocks">
				{'█'.repeat(filled)}
				<span className="coverage-map-bar__empty">{'░'.repeat(empty)}</span>
			</span>
			<span className="coverage-map-bar__pct">{coverage}%</span>
		</span>
	);
}

function NepalCoverageMapPage() {
	const sorted = [...COVERAGE_CATEGORIES].sort((a, b) => b.coverage - a.coverage);
	const critical = COVERAGE_CATEGORIES.filter((c) => c.status === 'critical_gap');
	const avgCoverage = Math.round(
		COVERAGE_CATEGORIES.reduce((sum, c) => sum + c.coverage, 0) / COVERAGE_CATEGORIES.length
	);

	return (
		<div className="explore-page">
			<Seo
				title="Nepal Coverage Map — Geographic Breadth"
				path="/coverage-map"
				noindex
				description="VisitAllNepal geographic and cultural coverage — provinces, districts, parks, villages, festivals, and underrepresented destinations across all of Nepal."
			/>
			<ExploreHero
				image={EXPLORE_IMAGES.valley}
				eyebrow="Editorial Mission"
				title="Nepal Coverage Map"
				lead="Everest is our depth benchmark. Nepal is our editorial strategy. This map tracks geographic and cultural breadth — every province, district, protected area, and underrepresented place we must eventually cover."
				compact
			/>
			<QuickFactsBar facts={[
				{ value: `${avgCoverage}%`, label: 'Avg category coverage' },
				{ value: '11/77', label: 'Districts covered' },
				{ value: String(critical.length), label: 'Critical gaps' },
				{ value: COVERAGE_MAP_META.lastAudit, label: 'Last audit' },
			]} />

			<div className="explore-body">
				<DiscoverySection
					title="Coverage by category"
					intro="Long-term goal: no meaningful destination in Nepal is missing from VisitAllNepal. Counts are honest — not aspirational."
				>
					<div className="coverage-map-grid">
						{sorted.map((cat) => (
							<div key={cat.id} className={`coverage-map-card coverage-map-card--${cat.status}`}>
								<div className="coverage-map-card__header">
									<Link to={cat.route} className="coverage-map-card__name">{cat.name}</Link>
									<span className="coverage-map-card__count">{cat.covered}/{cat.total}</span>
								</div>
								<CoverageBar coverage={cat.coverage} />
								<span className="coverage-map-card__status">{statusLabel(cat.status)}</span>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection eyebrow="Source hierarchy" title="How we research" alt>
					<div className="coverage-map-tiers">
						{SOURCE_TIERS.map((t) => (
							<div key={t.tier} className="coverage-map-tier">
								<span className="coverage-map-tier__label">Tier {t.tier} — {t.name}</span>
								<p className="coverage-map-tier__desc">{t.description}</p>
							</div>
						))}
					</div>
					<p className="coverage-map-note">
						Tier 3 (Instagram, YouTube, forums) is for experience and inspiration only — never for visa rules, permits, or fees.
					</p>
				</DiscoverySection>

				<DiscoverySection eyebrow="Breadth priorities" title="Underrepresented destinations">
					<p className="discovery-section__intro">
						Places with little high-quality centralized information elsewhere. Dolpo is the exemplar profile.
					</p>
					<div className="knowledge-index-table-wrap">
						<table className="knowledge-index-table">
							<thead>
								<tr>
									<th>Destination</th>
									<th>District</th>
									<th>Province</th>
									<th>Coverage</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{PRIORITY_BREADTH_TARGETS.map((d) => (
									<tr key={d.slug} className={d.coverage === 0 ? 'knowledge-index-table__row--critical_gap' : ''}>
										<td><strong>{d.name}</strong></td>
										<td>{d.district}</td>
										<td>{d.province}</td>
										<td>
											<CoverageBar coverage={d.coverage} />
										</td>
										<td>{statusLabel(d.status)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</DiscoverySection>

				<DiscoverySection eyebrow="Depth benchmark" title="Everest vs Nepal" alt>
					<div className="coverage-map-benchmark">
						<p>
							<strong>Depth:</strong> Everest Base Camp Guided Journey proves we can guide a traveler end-to-end with official citations and decision trees.
						</p>
						<p>
							<strong>Breadth:</strong> District pages, Dolpo profiles, Rara guides, and village festivals — the moat no competitor will build.
						</p>
						<div className="coverage-map-benchmark__links">
							<Link to="/journeys/everest-base-camp" className="explore-hero__cta">EBC Guided Journey →</Link>
							<Link to="/knowledge-index" className="explore-hero__cta explore-hero__cta--secondary">Topic depth index →</Link>
							<Link to="/provinces" className="explore-hero__cta explore-hero__cta--secondary">Explore by province →</Link>
						</div>
					</div>
				</DiscoverySection>
			</div>
		</div>
	);
}

export default NepalCoverageMapPage;
